import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Download as DownloadIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { AdminExportApi, DataExportJob } from "../../../../api/AdminExportApi";
import { useAuth } from "../../../../auth";

export const Route = createFileRoute("/__auth/admin/data-export/")({
  component: DataExportPage,
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin && !context.auth.hasPermission("dataexport.view")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  staticData: {
    title: "routes.dataExport",
  },
});

function DataExportPage() {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const canExecute = Boolean(user?.isAdmin || hasPermission("dataexport.execute"));
  const canDownload = Boolean(user?.isAdmin || hasPermission("dataexport.download"));
  const [jobs, setJobs] = useState<DataExportJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false, message: "", severity: "info",
  });

  const loadJobs = async () => {
    try {
      const data = await AdminExportApi.listExports();
      setJobs(data);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: t("dataExport.loadError"), severity: "error" });
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleRequestExport = async () => {
    setLoading(true);
    try {
      await AdminExportApi.requestExport();
      setSnackbar({ open: true, message: t("dataExport.requestSuccess"), severity: "success" });
      loadJobs();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: t("dataExport.requestError"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <Chip label={t("dataExport.statuses.completed")} color="success" size="small" />;
      case "PROCESSING":
        return <Chip label={t("dataExport.statuses.processing")} color="info" size="small" />;
      case "FAILED":
        return <Chip label={t("dataExport.statuses.failed")} color="error" size="small" />;
      default:
        return <Chip label={t("dataExport.statuses.pending")} color="warning" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {t("dataExport.title")}
        </Typography>
        {canExecute && (
          <Button
            variant="contained"
            onClick={handleRequestExport}
            disabled={loading}
            startIcon={<DownloadIcon />}
          >
            {t("dataExport.generateNew")}
          </Button>
        )}
      </Box>

      <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}>
        <CardHeader
          title={t("dataExport.instructionsTitle")}
          titleTypographyProps={{ variant: "h6" }}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {t("dataExport.instructions")}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("dataExport.historyTitle")}</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={loadJobs}>
          {t("dataExport.refreshList")}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell>{t("dataExport.table.id")}</TableCell>
              <TableCell>{t("dataExport.table.requestDate")}</TableCell>
              <TableCell>{t("common.status")}</TableCell>
              <TableCell>{t("dataExport.table.size")}</TableCell>
              <TableCell align="right">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {t("dataExport.table.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>#{job.id}</TableCell>
                  <TableCell>{new Date(job.createdAt).toLocaleString("pt-BR")}</TableCell>
                  <TableCell>{getStatusChip(job.status)}</TableCell>
                  <TableCell>
                    {job.fileSize ? `${(job.fileSize / 1024 / 1024).toFixed(2)} MB` : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {job.status === "COMPLETED" && canDownload && (
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={AdminExportApi.getDownloadUrl(job.id)}
                        download
                        target="_blank"
                      >
                        {t("dataExport.downloadZip")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
